import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { 
  Settings, 
  Save, 
  X, 
  Upload, 
  Plus, 
  Trash2, 
  Building2,
  Globe,
  MapPin,
  Phone,
  DollarSign,
  FileText,
  CreditCard,
  Eye,
  EyeOff,
  RefreshCw
} from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../../services/api';

const QuotationInvoiceDefaults = ({ onClose }) => {
  const [loading, setLoading] = useState(false);
  const [defaults, setDefaults] = useState({
    logo: '',
    footer_logo: '',
    company_name: 'Niswa Info Tech',
    company_website: '',
    address: '',
    city: '',
    state: '',
    country: '',
    pin_code: '',
    phone_number: '',
    company_email: '',
    default_taxes: [],
    show_tax: true,
    show_currency_symbol: true,
    default_currency: 'USD',
    show_total_in_words: true,
    total_in_words_format: 'normal',
    invoice_prefix: 'INV',
    next_invoice_number: 1,
    invoice_due_after_days: 30,
    show_project_name_invoice: true,
    show_total_paid_invoice: true,
    show_amount_due_invoice: true,
    attach_pdf_email: true,
    attach_pdf_whatsapp: true,
    default_invoice_client_note: '',
    default_invoice_terms: '',
    quotation_prefix: 'QUO',
    next_quotation_number: 1,
    quotation_due_after_days: 30,
    show_project_name_quotation: true,
    show_total_paid_quotation: true,
    show_amount_due_quotation: true,
    default_quotation_client_note: '',
    default_quotation_terms: '',
    quotation_info_format: {},
    invoice_info_format: {},
    default_payment_details: {},
    company_logo_url: ''
  });

  const [newTax, setNewTax] = useState({ name: '', rate: 0, enabled: true });
  const [showPassword, setShowPassword] = useState({});

  // File input refs for logo uploads
  const fileInputRef = React.useRef(null);
  const footerFileInputRef = React.useRef(null);

  const handleClickUploadLogo = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const handleClickUploadFooterLogo = () => {
    if (footerFileInputRef.current) footerFileInputRef.current.click();
  };

  const handleLogoFileChange = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    try {
      setLoading(true);
      // First try dedicated image endpoint with expected field name 'image'
      let response;
      try {
        const fdImage = new FormData();
        fdImage.append('image', file);
        response = await api.post('/upload/image', fdImage, { headers: { 'Content-Type': 'multipart/form-data' } });
      } catch (_) {
        // Fallback to generic file endpoint with field name 'file'
        const fdFile = new FormData();
        fdFile.append('file', file);
        response = await api.post('/upload/file', fdFile, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      const data = response?.data?.data || response?.data || {};
      const url = data?.url || data?.location || data?.path || data?.secure_url || data?.fileUrl || data?.file_url || '';
      if (url) setDefaults((prev) => ({ ...prev, logo: url, company_logo_url: url }));
    } catch (err) {
      console.error('Logo upload failed:', err);
    } finally {
      setLoading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleFooterLogoFileChange = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    try {
      setLoading(true);
      // First try dedicated image endpoint with expected field name 'image'
      let response;
      try {
        const fdImage = new FormData();
        fdImage.append('image', file);
        response = await api.post('/upload/image', fdImage, { headers: { 'Content-Type': 'multipart/form-data' } });
      } catch (_) {
        // Fallback to generic file endpoint with field name 'file'
        const fdFile = new FormData();
        fdFile.append('file', file);
        response = await api.post('/upload/file', fdFile, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      const data = response?.data?.data || response?.data || {};
      const url = data?.url || data?.location || data?.path || data?.secure_url || data?.fileUrl || data?.file_url || '';
      if (url) setDefaults((prev) => ({ ...prev, footer_logo: url }));
    } catch (err) {
      console.error('Footer logo upload failed:', err);
    } finally {
      setLoading(false);
      if (footerFileInputRef.current) footerFileInputRef.current.value = '';
    }
  };

  useEffect(() => {
    fetchDefaults();
  }, []);

  const fetchDefaults = async () => {
    try {
      const response = await api.get('/quotation-invoice-defaults');
      if (response.data.success) {
        const data = response.data.data;
        setDefaults((prev) => ({ ...prev, ...data, logo: data.logo || data.company_logo_url || '' }));
      }
    } catch (error) {
      console.error('Error fetching defaults:', error);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const payload = { ...defaults, logo: defaults.logo || defaults.company_logo_url || '' };
      await api.put('/quotation-invoice-defaults', payload);
      toast.success('Default settings saved successfully');
      onClose();
    } catch (error) {
      console.error('Error saving defaults:', error);
      toast.error('Failed to save default settings');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setDefaults(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const addTax = () => {
    if (newTax.name && newTax.rate > 0) {
      setDefaults(prev => ({
        ...prev,
        default_taxes: [...prev.default_taxes, { ...newTax }]
      }));
      setNewTax({ name: '', rate: 0, enabled: true });
    }
  };

  const removeTax = (index) => {
    setDefaults(prev => ({
      ...prev,
      default_taxes: prev.default_taxes.filter((_, i) => i !== index)
    }));
  };

  const updateTax = (index, field, value) => {
    setDefaults(prev => ({
      ...prev,
      default_taxes: prev.default_taxes.map((tax, i) => 
        i === index ? { ...tax, [field]: value } : tax
      )
    }));
  };

  return createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <Settings className="w-6 h-6" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Quotation & Invoice Defaults</h2>
          </div>
          <button onClick={onClose} className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6 space-y-6">
          {/* Company Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Company Logo</label>
              <div className="flex items-center gap-3">
                <button type="button" onClick={handleClickUploadLogo} className="px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm">
                  Upload Logo
                </button>
                <input
                  type="text"
                  value={defaults.logo || ''}
                  onChange={(e) => setDefaults((prev) => ({ ...prev, logo: e.target.value, company_logo_url: e.target.value }))}
                  placeholder="Paste logo URL"
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                />
              </div>
              {(defaults.logo || defaults.company_logo_url) && (
                <div className="mt-2">
                  <img
                    src={defaults.logo || defaults.company_logo_url}
                    alt="Company Logo Preview"
                    className="h-16 object-contain border border-gray-200 dark:border-gray-700 rounded"
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                </div>
              )}
              <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleLogoFileChange} />
            </div>
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Footer Logo</label>
              <div className="flex items-center gap-3">
                <button type="button" onClick={handleClickUploadFooterLogo} className="px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm">
                  Upload Footer Logo
                </button>
                <input
                  type="text"
                  value={defaults.footer_logo || ''}
                  onChange={(e) => setDefaults((prev) => ({ ...prev, footer_logo: e.target.value }))}
                  placeholder="Paste footer logo URL"
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                />
              </div>
              {defaults.footer_logo && (
                <div className="mt-2">
                  <img
                    src={defaults.footer_logo}
                    alt="Footer Logo Preview"
                    className="h-16 object-contain border border-gray-200 dark:border-gray-700 rounded"
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                </div>
              )}
              <input ref={footerFileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFooterLogoFileChange} />
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Company Name</label>
              <input
                type="text"
                value={defaults.company_name}
                onChange={(e) => setDefaults((prev) => ({ ...prev, company_name: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
          </div>

          {/* Company Information */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
              <Building2 className="w-5 h-5 mr-2" />
              Company Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Website
                </label>
                <input
                  type="url"
                  value={defaults.company_website}
                  onChange={(e) => handleInputChange('company_website', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Phone Number
                </label>
                <input
                  type="tel"
                  value={defaults.phone_number}
                  onChange={(e) => handleInputChange('phone_number', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Company Email
                </label>
                <input
                  type="email"
                  value={defaults.company_email}
                  onChange={(e) => handleInputChange('company_email', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Address
              </label>
              <textarea
                value={defaults.address}
                onChange={(e) => handleInputChange('address', e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  City
                </label>
                <input
                  type="text"
                  value={defaults.city}
                  onChange={(e) => handleInputChange('city', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  State
                </label>
                <input
                  type="text"
                  value={defaults.state}
                  onChange={(e) => handleInputChange('state', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Country
                </label>
                <input
                  type="text"
                  value={defaults.country}
                  onChange={(e) => handleInputChange('country', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Pin Code
                </label>
                <input
                  type="text"
                  value={defaults.pin_code}
                  onChange={(e) => handleInputChange('pin_code', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Currency & Tax Settings */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
              <DollarSign className="w-5 h-5 mr-2" />
              Currency & Tax Settings
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Default Currency
                </label>
                <select
                  value={defaults.default_currency}
                  onChange={(e) => handleInputChange('default_currency', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                >
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                  <option value="GBP">GBP</option>
                  <option value="INR">INR</option>
                  <option value="CAD">CAD</option>
                  <option value="AUD">AUD</option>
                </select>
              </div>
              <div className="flex items-center space-x-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={defaults.show_tax}
                    onChange={(e) => handleInputChange('show_tax', e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Show Tax</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={defaults.show_currency_symbol}
                    onChange={(e) => handleInputChange('show_currency_symbol', e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Show Currency Symbol</span>
                </label>
              </div>
              <div className="flex items-center space-x-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={defaults.show_total_in_words}
                    onChange={(e) => handleInputChange('show_total_in_words', e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Show Total in Words</span>
                </label>
              </div>
            </div>

            {/* Tax Management */}
            <div className="mt-6">
              <h4 className="text-md font-medium text-gray-900 dark:text-white mb-3">Default Taxes</h4>
              <div className="space-y-3">
                {defaults.default_taxes.map((tax, index) => (
                  <div key={index} className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <input
                      type="text"
                      value={tax.name}
                      onChange={(e) => updateTax(index, 'name', e.target.value)}
                      placeholder="Tax Name"
                      className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                    />
                    <input
                      type="number"
                      value={tax.rate}
                      onChange={(e) => updateTax(index, 'rate', parseFloat(e.target.value))}
                      placeholder="Rate %"
                      step="0.01"
                      className="w-24 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                    />
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={tax.enabled}
                        onChange={(e) => updateTax(index, 'enabled', e.target.checked)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Enabled</span>
                    </label>
                    <button
                      onClick={() => removeTax(index)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                
                <div className="flex items-center space-x-3 p-3 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg">
                  <input
                    type="text"
                    value={newTax.name}
                    onChange={(e) => setNewTax(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Tax Name"
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                  />
                  <input
                    type="number"
                    value={newTax.rate}
                    onChange={(e) => setNewTax(prev => ({ ...prev, rate: parseFloat(e.target.value) }))}
                    placeholder="Rate %"
                    step="0.01"
                    className="w-24 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                  />
                  <button
                    onClick={addTax}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Invoice Settings */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
              <FileText className="w-5 h-5 mr-2" />
              Invoice Settings
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Invoice Prefix
                </label>
                <input
                  type="text"
                  value={defaults.invoice_prefix}
                  onChange={(e) => handleInputChange('invoice_prefix', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Next Invoice Number
                </label>
                <input
                  type="number"
                  value={defaults.next_invoice_number}
                  onChange={(e) => handleInputChange('next_invoice_number', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Invoice Due After (Days)
                </label>
                <input
                  type="number"
                  value={defaults.invoice_due_after_days}
                  onChange={(e) => handleInputChange('invoice_due_after_days', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Total in Words Format
                </label>
                <select
                  value={defaults.total_in_words_format}
                  onChange={(e) => handleInputChange('total_in_words_format', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                >
                  <option value="normal">Normal</option>
                  <option value="lowercase">Lowercase</option>
                </select>
              </div>
            </div>

            <div className="mt-4 space-y-3">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={defaults.show_project_name_invoice}
                  onChange={(e) => handleInputChange('show_project_name_invoice', e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Show Project Name on Invoice</span>
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={defaults.show_total_paid_invoice}
                  onChange={(e) => handleInputChange('show_total_paid_invoice', e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Show Total Paid on Invoice</span>
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={defaults.show_amount_due_invoice}
                  onChange={(e) => handleInputChange('show_amount_due_invoice', e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Show Amount Due on Invoice</span>
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={defaults.attach_pdf_email}
                  onChange={(e) => handleInputChange('attach_pdf_email', e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Attach Invoice PDF when sending Payment Receipt to Email</span>
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={defaults.attach_pdf_whatsapp}
                  onChange={(e) => handleInputChange('attach_pdf_whatsapp', e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Attach Invoice PDF when sending Payment Receipt to WhatsApp</span>
              </label>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Default Invoice Client Note
              </label>
              <textarea
                value={defaults.default_invoice_client_note}
                onChange={(e) => handleInputChange('default_invoice_client_note', e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Default Invoice Terms & Conditions
              </label>
              <textarea
                value={defaults.default_invoice_terms}
                onChange={(e) => handleInputChange('default_invoice_terms', e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Quotation Settings */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
              <FileText className="w-5 h-5 mr-2" />
              Quotation Settings
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Quotation Prefix
                </label>
                <input
                  type="text"
                  value={defaults.quotation_prefix}
                  onChange={(e) => handleInputChange('quotation_prefix', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Next Quotation Number
                </label>
                <input
                  type="number"
                  value={defaults.next_quotation_number}
                  onChange={(e) => handleInputChange('next_quotation_number', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Quotation Due After (Days)
                </label>
                <input
                  type="number"
                  value={defaults.quotation_due_after_days}
                  onChange={(e) => handleInputChange('quotation_due_after_days', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="mt-4 space-y-3">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={defaults.show_project_name_quotation}
                  onChange={(e) => handleInputChange('show_project_name_quotation', e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Show Project Name on Quotation</span>
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={defaults.show_total_paid_quotation}
                  onChange={(e) => handleInputChange('show_total_paid_quotation', e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Show Total Paid on Quotation</span>
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={defaults.show_amount_due_quotation}
                  onChange={(e) => handleInputChange('show_amount_due_quotation', e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Show Amount Due on Quotation</span>
              </label>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Default Quotation Client Note
              </label>
              <textarea
                value={defaults.default_quotation_client_note}
                onChange={(e) => handleInputChange('default_quotation_client_note', e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Default Quotation Terms & Conditions
              </label>
              <textarea
                value={defaults.default_quotation_terms}
                onChange={(e) => handleInputChange('default_quotation_terms', e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Payment Details */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
              <CreditCard className="w-5 h-5 mr-2" />
              Default Payment Details
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Payment QR Code
                </label>
                <input
                  type="text"
                  value={defaults.default_payment_details.payment_qr_code || ''}
                  onChange={(e) => handleInputChange('default_payment_details', {
                    ...defaults.default_payment_details,
                    payment_qr_code: e.target.value
                  })}
                  placeholder="QR Code URL or data"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Bank Account Details
                </label>
                <textarea
                  value={defaults.default_payment_details.bank_account_details || ''}
                  onChange={(e) => handleInputChange('default_payment_details', {
                    ...defaults.default_payment_details,
                    bank_account_details: e.target.value
                  })}
                  rows={3}
                  placeholder="Bank name, account number, IFSC code, etc."
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  UPI ID
                </label>
                <input
                  type="text"
                  value={defaults.default_payment_details.upi_id || ''}
                  onChange={(e) => handleInputChange('default_payment_details', {
                    ...defaults.default_payment_details,
                    upi_id: e.target.value
                  })}
                  placeholder="yourname@bank"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Payment Link
                </label>
                <input
                  type="url"
                  value={defaults.default_payment_details.payment_link || ''}
                  onChange={(e) => handleInputChange('default_payment_details', {
                    ...defaults.default_payment_details,
                    payment_link: e.target.value
                  })}
                  placeholder="https://payment.gateway.com/..."
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end space-x-3 p-6 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-500"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={loading}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {loading ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Save Settings
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default QuotationInvoiceDefaults;
